import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area, PieChart, Pie, Cell, Legend, ReferenceLine } from "recharts";
import { BarChart3, PieChart as PieIcon, TrendingUp, GraduationCap } from "lucide-react";

const CADMIUM = "#2E9CFF";
const MOSS = "#2BB875";
const AMBER = "#FFB340";
const INK = "#0F172A";
const MUTED = "#64748B";

const tooltipProps = {
  contentStyle: { background: "#FFFFFF", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, fontFamily: "monospace", fontSize: 12 },
  labelStyle: { color: INK },
  itemStyle: { color: INK },
};

const axisTick = { fill: MUTED, fontSize: 11, fontFamily: "monospace" };
const moneyShort = (n) => (n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n}`);
const moneyFull = (v) => `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

const PASS_MARK = 85;
// Sample COC certification exam results (demo data): 45 tested, 13 at or below the 85% pass mark
const TRAINING = [
  { name: "Nina Alvarez", score: 100 },
  { name: "Derek Foster", score: 98 },
  { name: "Zoe Franklin", score: 98 },
  { name: "Aisha Bello", score: 97 },
  { name: "Tom Becker", score: 96 },
  { name: "Sofia Marino", score: 96 },
  { name: "Caleb Wright", score: 95 },
  { name: "Yara Saleh", score: 95 },
  { name: "Dana Kim", score: 94 },
  { name: "Luis Herrera", score: 94 },
  { name: "Carla Jensen", score: 94 },
  { name: "Emily Zhang", score: 93 },
  { name: "Jordan Pike", score: 93 },
  { name: "Fatima Sy", score: 92 },
  { name: "Greg Lawson", score: 92 },
  { name: "Ximena Cruz", score: 92 },
  { name: "Hannah Roy", score: 91 },
  { name: "Ivan Petrov", score: 91 },
  { name: "Julia Santos", score: 90 },
  { name: "Kevin O'Brien", score: 90 },
  { name: "Laura Bennett", score: 89 },
  { name: "Miguel Torres", score: 89 },
  { name: "Brian Okafor", score: 89 },
  { name: "Nora Haddad", score: 88 },
  { name: "Owen Blake", score: 88 },
  { name: "Paula Reyes", score: 88 },
  { name: "Quincy Adams", score: 87 },
  { name: "Rosa Delgado", score: 87 },
  { name: "Sean Murphy", score: 87 },
  { name: "Tara Iyer", score: 86 },
  { name: "Umar Farouk", score: 86 },
  { name: "Vera Kovac", score: 86 },
  { name: "Marcus Bell", score: 85 },
  { name: "Tanya Ruiz", score: 83 },
  { name: "Devon Carter", score: 81 },
  { name: "Priya Nair", score: 80 },
  { name: "Sam Whitfield", score: 78 },
  { name: "Lena Ortiz", score: 77 },
  { name: "Jamal Hughes", score: 76 },
  { name: "Grace Muller", score: 74 },
  { name: "Andre Baptiste", score: 72 },
  { name: "Kelsey Dunn", score: 71 },
  { name: "Ravi Patel", score: 68 },
  { name: "Monica Sweeney", score: 65 },
  { name: "Travis Cole", score: 62 },
];

const ChartCard = ({ icon: Icon, title, note, children, testid, className = "" }) => (
  <div className={`technical-card p-6 ${className}`} data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <Icon size={14} className="text-sky-600" />
      <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900">{title}</p>
    </div>
    {note && <p className="text-[11px] text-muted-foreground mb-4">{note}</p>}
    {children}
  </div>
);

const StatChip = ({ label, value, tone }) => (
  <span className="inline-flex items-center gap-1.5 rounded-sm border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
    {label}
    <span className={`font-semibold normal-case text-xs ${tone || "text-slate-900"}`}>{value}</span>
  </span>
);

export const KpiCharts = ({ kpis, collections, history, training }) => {
  const collData = (collections || []).map((c) => ({
    month: c.month,
    Due: Math.round(c.due * 100) / 100,
    Received: Math.round(c.received * 100) / 100,
  }));

  const occupied = kpis?.occupied_units ?? 0;
  const vacant = kpis?.vacant_units ?? 0;
  const mixReady = occupied + vacant > 0;
  const mix = [
    { name: "Occupied", value: occupied },
    { name: "Vacant", value: vacant },
  ];
  const occupancyPct = mixReady ? Math.round((occupied / (occupied + vacant)) * 100) : 0;

  const trendData = (history || [])
    .filter((h) => h.total_units)
    .map((h) => ({
      at: new Date(h.synced_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      Occupancy: Math.round((h.occupied_units / h.total_units) * 1000) / 10,
      "Collection rate": h.collection_rate_current ?? null,
    }));

  const liveTraining = training?.results?.length ? training : null;
  const passMark = liveTraining?.pass_mark ?? PASS_MARK;
  const trainingData = [...(liveTraining ? liveTraining.results : TRAINING)].sort((a, b) => b.score - a.score);
  const failing = trainingData.filter((t) => t.score <= passMark);
  const avgScore = Math.round(trainingData.reduce((s, t) => s + t.score, 0) / trainingData.length);

  if (!collData.length && !mixReady) return null;

  return (
    <section className="mb-10" data-testid="portal-kpi-charts">
      <p className="text-xs font-mono font-semibold uppercase tracking-[0.24em] text-muted-foreground mb-4">
        Portfolio Performance — KPI Charts
      </p>
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        {collData.length > 0 && (
          <ChartCard icon={BarChart3} title="Collections by Month" note="Rent due vs received per rental month" testid="chart-collections" className="lg:col-span-2">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={collData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tick={axisTick} axisLine={{ stroke: "#CBD5E1" }} tickLine={false} />
                <YAxis tickFormatter={moneyShort} tick={axisTick} axisLine={false} tickLine={false} width={56} />
                <Tooltip {...tooltipProps} formatter={(v) => [moneyFull(v)]} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                <Legend wrapperStyle={{ fontFamily: "monospace", fontSize: 11 }} />
                <Bar dataKey="Due" fill={AMBER} radius={[3, 3, 0, 0]} maxBarSize={44} />
                <Bar dataKey="Received" fill={MOSS} radius={[3, 3, 0, 0]} maxBarSize={44} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
        {mixReady && (
          <ChartCard icon={PieIcon} title="Unit Mix" note="Occupied vs vacant units" testid="chart-unit-mix" className={collData.length ? "" : "lg:col-span-3"}>
            <div className="relative">
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={mix} dataKey="value" nameKey="name" innerRadius={62} outerRadius={88} strokeWidth={3} stroke="#07090D" paddingAngle={3}>
                    <Cell fill={CADMIUM} />
                    <Cell fill={AMBER} />
                  </Pie>
                  <Tooltip {...tooltipProps} />
                  <Legend wrapperStyle={{ fontFamily: "monospace", fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ marginBottom: 28 }}>
                <span className="font-mono text-3xl font-semibold text-slate-900">{occupancyPct}%</span>
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">occupied</span>
              </div>
            </div>
          </ChartCard>
        )}
      </div>

      <ChartCard
        icon={GraduationCap}
        title="Training — Certification Scores"
        note={liveTraining ? `Live · ${liveTraining.exam} · pass mark ${passMark}%` : "Sample data · import real COC results above to go live · pass mark 85%"}
        testid="chart-training"
      >
        <div className="flex flex-wrap gap-2 mb-5" data-testid="training-stat-chips">
          <StatChip label="Tested" value={trainingData.length} />
          <StatChip label="Passed" value={trainingData.length - failing.length} tone="text-green-700" />
          <StatChip label="Needs Retake" value={failing.length} tone="text-amber-700" />
          <StatChip label="Avg Score" value={`${avgScore}%`} />
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={trainingData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="name" tick={false} axisLine={{ stroke: "#CBD5E1" }} tickLine={false} />
            <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={axisTick} axisLine={false} tickLine={false} width={44} />
            <Tooltip {...tooltipProps} formatter={(v) => [`${v}%`, "Score"]} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
            <ReferenceLine y={passMark} stroke={CADMIUM} strokeDasharray="5 4" label={{ value: `pass ${passMark}%`, position: "insideTopRight", fill: CADMIUM, fontSize: 10, fontFamily: "monospace" }} />
            <Bar dataKey="score" radius={[3, 3, 0, 0]} maxBarSize={18}>
              {trainingData.map((t) => (
                <Cell key={t.name} fill={t.score <= passMark ? AMBER : MOSS} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-5" data-testid="training-retake-list">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
            Needs retake ({failing.length}) — scored {passMark}% or below
          </p>
          <div className="flex flex-wrap gap-1.5">
            {failing.map((t) => (
              <span key={t.name} className="rounded-sm border border-amber/30 bg-amber/10 px-2 py-1 text-[10px] font-mono text-amber-700">
                {t.name} · {t.score}%
              </span>
            ))}
          </div>
        </div>
      </ChartCard>

      <div className="mt-4">
        <ChartCard icon={TrendingUp} title="Occupancy & Collection Trend" note="One data point per sync — press Sync Now over time to build the trend" testid="chart-trend">
          {trendData.length >= 2 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={trendData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="occGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CADMIUM} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={CADMIUM} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="at" tick={axisTick} axisLine={{ stroke: "#CBD5E1" }} tickLine={false} />
                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={axisTick} axisLine={false} tickLine={false} width={48} />
                <Tooltip {...tooltipProps} formatter={(v) => [`${v}%`]} />
                <Legend wrapperStyle={{ fontFamily: "monospace", fontSize: 11 }} />
                <Area type="monotone" dataKey="Occupancy" stroke={CADMIUM} strokeWidth={2} fill="url(#occGrad)" />
                <Area type="monotone" dataKey="Collection rate" stroke={MOSS} strokeWidth={2} fill="none" strokeDasharray="5 4" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex flex-col items-center justify-center border border-dashed border-slate-200 rounded-md" data-testid="chart-trend-empty">
              <TrendingUp size={20} className="text-muted-foreground/40 mb-2" />
              <p className="text-xs text-muted-foreground">Trend needs at least two syncs.</p>
              <p className="mt-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground/60">Each Sync Now adds a data point.</p>
            </div>
          )}
        </ChartCard>
      </div>
    </section>
  );
};
